import { z } from 'zod';
import { t as tGlobal } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useFormHook } from '@Hooks/index.ts';
import { useNavigationManager } from '@Router/useNavigationManager';
import { useBoundStore } from '@Store/useBoundStore';
import { RoutesMap } from '@Router/router.types';
import { worldCountires } from '@Pages/happyFlowPages/eddQuestions/hooks/worldCountries';
import { usePostEddData } from '@Pages/happyFlowPages/eddQuestions/hooks/usePostEddData';
import { EddEmbeddedFields } from '../fields.types';
import { getDefaultValueByQuestionId, saveEddQuestionToLocalStore } from '../../../shared.utils';
import { EDD_VERSION } from '../../../consts';
export const useCountriesBusinessWithLogic = () => {
  const { getEddEmbeddedQuestions } = useBoundStore((state) => state);
  const { mutateAsync: postEddData, isPending: isPostingEddData } = usePostEddData();
  const { navigateToNextPage } = useNavigationManager();
  const { t } = useTranslation();
  const defaultGroupValues = {
    US: { title: 'United States', value: 'US' },
    OTHER: { title: 'Other', value: 'OTHER' },
  };

  const countryDataList = worldCountires;

  const formMethods = useFormHook({
    schemaTitle: 'countries-business-with',
    formSchema: z.object({
      [EddEmbeddedFields.EDD_EMBEDDED_CUSTOMER_LOCATION]: z
        .string({ error: tGlobal('enErrors:required') })
        .min(1, { error: tGlobal('enErrors:required') })
        .refine((val) => val !== 'OTHER', { error: tGlobal('enErrors:required') }),
      [EddEmbeddedFields.EDD_EMBEDDED_VENDOR_LOCATION]: z
        .string({ error: tGlobal('enErrors:required') })
        .min(1, { error: tGlobal('enErrors:required') })
        .refine((val) => val !== 'OTHER', { error: tGlobal('enErrors:required') }),
    }),
    formDefaultValues: {
      [EddEmbeddedFields.EDD_EMBEDDED_CUSTOMER_LOCATION]:
        getDefaultValueByQuestionId(EddEmbeddedFields.EDD_EMBEDDED_CUSTOMER_LOCATION) || '',
      [EddEmbeddedFields.EDD_EMBEDDED_VENDOR_LOCATION]:
        getDefaultValueByQuestionId(EddEmbeddedFields.EDD_EMBEDDED_VENDOR_LOCATION) || '',
    },
  });

  const reactiveCustomerCountry = formMethods.watch(
    EddEmbeddedFields.EDD_EMBEDDED_CUSTOMER_LOCATION
  );

  const reactiveVendorCountry = formMethods.watch(EddEmbeddedFields.EDD_EMBEDDED_VENDOR_LOCATION);

  const selectedCustomerCountry = worldCountires?.find(
    (country) => country.value === reactiveCustomerCountry
  );

  const selectedVendorCountry = worldCountires?.find(
    (country) => country.value === reactiveVendorCountry
  );
  const handleSubmit = async () => {
    /** Save to local store */
    saveEddQuestionToLocalStore({
      question: t('countriesDoingBusinessWith.groupATitle'),
      questionId: EddEmbeddedFields.EDD_EMBEDDED_CUSTOMER_LOCATION,
      answer: formMethods.getValues(EddEmbeddedFields.EDD_EMBEDDED_CUSTOMER_LOCATION),
    });
    saveEddQuestionToLocalStore({
      question: t('countriesDoingBusinessWith.groupBTitle'),
      questionId: EddEmbeddedFields.EDD_EMBEDDED_VENDOR_LOCATION,
      answer: formMethods.getValues(EddEmbeddedFields.EDD_EMBEDDED_VENDOR_LOCATION),
    });

    /** Save to Server */
    try {
      await postEddData({
        version: EDD_VERSION,
        results: getEddEmbeddedQuestions(),
      });
      navigateToNextPage(RoutesMap.EDD_EMBEDDED_BUSINESS_COUNTRIES);
    } catch (error) {
      /** TODO: Handle error - Check with PM */
      console.error(error);
    }
  };

  return {
    countryDataList,
    formMethods,
    selectedCustomerCountry,
    selectedVendorCountry,
    defaultGroupValues,
    handleSubmit,
    isLoading: isPostingEddData,
  };
};
